<template>
  <form @submit.prevent="submit">
    <Alert :message="errors" variant="danger" v-if="errors" />
    <div class="mb-5">
      <FormInput
        name="name"
        v-model="fields.name"
        :label="`${$t('global.team-name')} *`"
        :errors="validation.fields.name.errors"
        :dirty="validation.fields.name.dirty"
      />
    </div>
    <template v-if="!isSuperUser">
      <LoadingState v-if="loadingPermissions" />
      <template v-else>
        <div class="teams-set mb-5">
          <h4>{{ $t("global.notifications") }}</h4>
          <div class="mb-4">
            {{ $t("global.select-team-notification-types") }}
          </div>
          <div
            class="team-toggle"
            v-for="(role, t) in notificationRoles"
            :key="`notification-${t}`"
          >
            <Toggle
              @change="(enabled) => toggleNotification(enabled, role)"
              :value="notificationIsToggled(role)"
            />
            <div>{{ $t(`global.role-name-${role.name}`) }}</div>
          </div>
        </div>
        <div>
          <h4>{{ $t("global.permissions") }}</h4>
          <div class="mb-4">
            {{ $t("global.select-team-notification-types") }}
          </div>
          <div class="form-row">
            <div class="col-md-4 mb-4" v-for="(role, i) in resourceRoles" :key="`resource-${i}`">
              <h4 class="mb-2">{{ $t(`global.role-name-${role.name}`) }}:</h4>
              <FormCheckbox
                @input="(checked) => togglePermission(checked, 'read', role.name)"
                name="read"
                :value="permissionIsChecked(role, 'read')"
                :label="`${$t('global.view')}`"
              />
              <FormCheckbox
                @input="(checked) => togglePermission(checked, 'write', role.name)"
                name="write"
                :value="permissionIsChecked(role, 'write')"
                :label="`${$t('global.edit')}`"
              />
              <FormCheckbox
                @input="(checked) => togglePermission(checked, 'delete', role.name)"
                name="delete"
                :value="permissionIsChecked(role, 'delete')"
                :label="`${$t('global.delete')}`"
              />
            </div>
          </div>
        </div>
      </template>
    </template>
    <div class="form-buttons">
      <Btn class="mr-2" variant="light" type="button" :disabled="loading" @click="cancel">
        {{ $t("global.cancel") }}
      </Btn>
      <Btn variant="primary" type="submit" :disabled="!isSubmittable()" :loading="loading">
        {{ $t("global.create") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Alert from "@/components/display/Alert";
import Btn from "@/components/display/Btn";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormInput from "@/components/display/form/FormInput";
import { isEqual } from "lodash-es";
import LoadingState from "@/components/display/LoadingState";
import store from "@/store";
import Toggle from "@/components/display/Toggle";
import useValidator from "@/utils/useValidator";
import { teamAdd as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "TeamAddForm",
  props: {
    team: Object,
  },
  components: {
    Alert,
    Btn,
    FormInput,
    Toggle,
    LoadingState,
    FormCheckbox,
  },
  setup(props, { emit, root }) {
    const loading = ref(false);
    const errors = ref(null);
    const rolesStatus = computed(() => store.getters["team/rolesStatus"]);
    const resourceRoles = computed(() => store.getters["team/resourceRoles"]);
    const notificationRoles = computed(() => store.getters["team/notificationRoles"]);
    const fields = reactive({
      name: props.team?.name,
      permissions: {},
      notifications: {},
    });

    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        name: {
          required: root.$t("validation.name-required"),
        },
      }
    );

    // form is dirty
    const dirty = ref(true);

    // detect if any permissions have been changed (compare props with new values)
    const permissionsDirty = () =>
      !resourceRoles.value.every((role) =>
        isEqual(fields.permissions[role.name], props.team.permissions[role.name])
      );

    // detect if any notifications have been changed (compare props with new values)
    const notificationsDirty = () => {
      const includedRoles = {};
      notificationRoles.value.forEach((role) => {
        includedRoles[role.category] = includedRoles[role.category] || [];
        if (props.team.permissions[role.name].length) {
          includedRoles[role.category].push(role.name);
        }
      });

      return !Object.keys(fields.notifications).every((category) =>
        isEqual(fields.notifications[category], includedRoles[category])
      );
    };

    // detect if the form is dirty (for editing existing teams only)
    const refreshDirty = () => {
      if (!isEditing.value || loadingPermissions.value) {
        dirty.value = true;
        return;
      }
      dirty.value = permissionsDirty() || notificationsDirty();
    };

    const cancel = () => emit("cancel");

    // check if the form is submittable
    const isSubmittable = () => dirty.value && !loading.value && validation.errors.length === 0;

    // get initial value of permission checkbox
    const permissionIsChecked = (role, action) => {
      if (fields.permissions[role.name]) {
        return fields.permissions[role.name].includes(action);
      }
      return false;
    };

    // get initial value of toggle
    const notificationIsToggled = (role) => fields.notifications[role.category].includes(role.name);

    // check if form is in edit mode (for editing existing teams)
    const isEditing = computed(() => !!props.team);

    // if the form is in edit mode, check if user is a super user (cannot update permissions on super users)
    const isSuperUser = computed(() => isEditing.value && !!props.team?.superuser);

    // initial values for fields are still being assigned
    const initializingFields = ref(true);

    // loading roles, and setting initial field values
    const loadingPermissions = computed(
      () => rolesStatus.value.isLoading || initializingFields.value
    );

    // add or remove permission from resource on team (read/write/delete)
    const togglePermission = (checked, permission, roleName) => {
      if (!checked && fields.permissions[roleName].includes(permission)) {
        const index = fields.permissions[roleName].findIndex((perm) => perm === permission);
        fields.permissions[roleName].splice(index, 1);
      } else if (checked) {
        fields.permissions[roleName].push(permission);
      }
      refreshDirty();
    };

    // add or remove notification for resource on team
    const toggleNotification = (enabled, role) => {
      if (!enabled && fields.notifications[role.category].includes(role.name)) {
        const index = fields.notifications[role.category].findIndex(
          (roleName) => roleName === role.name
        );
        fields.notifications[role.category].splice(index, 1);
      } else if (enabled) {
        fields.notifications[role.category].push(role.name);
      }
      refreshDirty();
    };

    // create a new team
    const createTeam = () =>
      store
        .dispatch("team/create", {
          name: fields.name,
          permissions: fields.permissions,
          notifications: fields.notifications,
        })
        // .then(() => store.dispatch("team/getList", { forceUpdate: true }))
        .catch((e) => {
          errors.value = e;
        });

    // edit an existing team
    const editTeam = () =>
      store
        .dispatch("team/edit", {
          teamId: props.team.id,
          name: fields.name,
          permissions: fields.permissions,
          notifications: fields.notifications,
        })
        // .then(() => store.dispatch("team/getList", { forceUpdate: true }))
        .catch((e) => {
          errors.value = e;
        });

    // submit the form
    const submit = () => {
      errors.value = null;
      validation
        .validate()
        .then(() => {
          loading.value = true;

          if (!isEditing.value) {
            return createTeam();
          } else {
            return editTeam();
          }
        })
        .then(() => {
          store.dispatch("toasts/addToast", {
            variant: "success",
            message: root.$t(
              !isEditing.value ? "toasts.team-added-message" : "toasts.team-edited-message"
            ),
          });
          emit("success");
        })
        .catch(() => {})
        .finally(() => {
          loading.value = false;
        });
    };

    onMounted(() => {
      store.dispatch("team/getRoles").then(() => {
        // set initial field values for permissions
        resourceRoles.value.forEach((role) => {
          let initialPermissions = [];
          // form is in edit mode, pre-fill fields with team's permission data
          if (isEditing.value) {
            // copy array to avoid passing a reference around (only a problem for empty initial permissions)
            initialPermissions = [...props.team.permissions[role.name]];
          }

          fields.permissions[role.name] = initialPermissions;
        });

        // set inital field values for team's notification settings
        // let initialNotifications = [];
        notificationRoles.value.forEach((role) => {
          fields.notifications[role.category] = fields.notifications[role.category]
            ? fields.notifications[role.category]
            : [];
          // form is in edit mode, pre-fill fields with team's notification data
          if (isEditing.value) {
            if (props.team.permissions[role.name].length) {
              // notifications are sorted into categories
              fields.notifications[role.category].push(role.name);
            }
          }
        });
        initializingFields.value = false;
      });
    });

    return {
      fields,
      errors,
      loading,
      loadingPermissions,
      submit,
      validation,
      cancel,
      isSubmittable,
      resourceRoles,
      notificationRoles,
      rolesStatus,
      togglePermission,
      toggleNotification,
      isSuperUser,
      permissionIsChecked,
      notificationIsToggled,
    };
  },
};
</script>

<style lang="scss" scoped>
.plan-type {
  font-size: 1.5rem;
}
.plan-price {
  font-size: 2.5rem;
}
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}

.teams-set {
  margin-top: 1rem;
  .team-toggle {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    font-size: 1rem;
    margin: 1rem 0;
    padding-bottom: 1rem;
    border-width: 0;
    border-bottom-width: 1px;
    border-style: solid;
    border-color: rgba(0, 0, 0, 0.3);
  }
}
</style>
